<div class="flex flex-row gap-4">
  <div class="flex-1 flex flex-col gap-4">
    <div class="card shadow-sm">
      <div class="card-body p-3 gap-2">
        <div class="type-section-title">Gameplay</div>

        <div class="form-control">
          <label class="label cursor-pointer label-option">
            <span class="label-text">
              Run in Background
              <app-icon
                name="gameHelp"
                size="inline"
                class="text-lighter ml-1"
                [tp]="'Keeps the game running while it is minimized or in another tab.'"
                [tpPlacement]="'top'"
              />
            </span>
            <input
              type="checkbox"
              [checked]="getOption('runInBackground')"
              class="checkbox"
              (click)="
                setOption('runInBackground', !getOption('runInBackground'))
              "
            />
          </label>
        </div>

        <div class="form-control">
          <label class="label cursor-pointer label-option">
            <span class="label-text">Backdrop Grid</span>
            <input
              type="checkbox"
              [checked]="getOption('showBackdropGrid')"
              class="checkbox"
              (click)="
                setOption('showBackdropGrid', !getOption('showBackdropGrid'))
              "
            />
          </label>
        </div>

        <div class="form-control">
          <label class="label cursor-pointer label-option">
            <span class="label-text">Always Expand Party View</span>
            <input
              type="checkbox"
              [checked]="getOption('partyViewAlwaysExpand')"
              class="checkbox"
              (click)="
                setOption(
                  'partyViewAlwaysExpand',
                  !getOption('partyViewAlwaysExpand')
                )
              "
            />
          </label>
        </div>

        <div class="form-control">
          <label class="label cursor-pointer label-option">
            <span class="label-text">Always Expand Craft View</span>
            <input
              type="checkbox"
              [checked]="getOption('craftingViewAlwaysExpand')"
              class="checkbox"
              (click)="
                setOption(
                  'craftingViewAlwaysExpand',
                  !getOption('craftingViewAlwaysExpand')
                )
              "
            />
          </label>
        </div>

        <div class="form-control">
          <label class="label cursor-pointer label-option">
            <span class="label-text">Adventure Log Overlay</span>
            <input
              type="checkbox"
              [checked]="getOption('adventureLogOverlay')"
              class="checkbox"
              (click)="
                setOption(
                  'adventureLogOverlay',
                  !getOption('adventureLogOverlay')
                )
              "
            />
          </label>
        </div>

        @if (getOption('adventureLogOverlay')) {
          <div class="flex flex-col gap-1">
            @for (kind of adventureLogKinds; track kind) {
              <div class="form-control">
                <label class="label cursor-pointer label-option">
                  <span class="label-text pl-6">{{ kind }} Messages</span>
                  <input
                    type="checkbox"
                    [checked]="getOption('adventureLogOverlayKinds')[kind]"
                    class="checkbox checkbox-sm"
                    (click)="toggleAdventureLogOverlayKind(kind)"
                  />
                </label>
              </div>
            }
          </div>
        }
      </div>
    </div>

    @if (isElectron && windowState()) {
      <div class="card shadow-sm">
        <div class="card-body p-3 gap-2">
          <div class="type-section-title">Display</div>

          <div class="form-control">
            <label class="label label-option">
              <span class="label-text">Display Mode</span>
              <ng-select
                [items]="displayModeChoices"
                bindLabel="label"
                bindValue="value"
                [clearable]="false"
                [searchable]="false"
                [(ngModel)]="displayMode"
                (ngModelChange)="setDisplayMode($event)"
              ></ng-select>
              <app-icon
                name="gameHelp"
                size="inline"
                class="text-lighter"
                [tp]="'F11 or Alt+Enter also toggles fullscreen.'"
                [tpPlacement]="'top'"
              />
            </label>
          </div>

          @if (displayMode() === 'windowed') {
            <div class="form-control">
              <label class="label label-option">
                <span class="label-text">Window Size</span>
                <ng-select
                  [items]="windowSizeChoices()"
                  bindLabel="label"
                  bindValue="id"
                  [clearable]="false"
                  [searchable]="false"
                  [(ngModel)]="windowSizeId"
                  (ngModelChange)="setWindowSize($event)"
                ></ng-select>
                <app-icon
                  name="gameHelp"
                  size="inline"
                  class="text-lighter"
                  [tp]="'Sizes the game area of the window in screen pixels. Only sizes that fit your current display are listed. Use UI Scale to make the interface itself bigger.'"
                  [tpPlacement]="'top'"
                />
              </label>
            </div>
          }
        </div>
      </div>
    }

    <div class="card shadow-sm">
      <div class="card-body p-3 gap-2">
        <div class="type-section-title">Performance</div>

        <div class="form-control">
          <label class="label label-option">
            <span class="label-text">
              Map Frame Rate
              <app-icon
                name="gameHelp"
                size="inline"
                class="text-lighter"
                [tp]="'How often the world map redraws. Lower values use less power. Does not affect game speed.'"
                [tpPlacement]="'top'"
              />
            </span>
            <ng-select
              [items]="mapFrameRateChoices"
              bindLabel="label"
              bindValue="value"
              [clearable]="false"
              [searchable]="false"
              [(ngModel)]="mapFrameRate"
              (ngModelChange)="setOption('mapFrameRate', $event)"
            ></ng-select>
          </label>
        </div>

        <div class="form-control">
          <label class="label cursor-pointer label-option">
            <span class="label-text">
              Background Throttle
              <app-icon
                name="gameHelp"
                size="inline"
                class="text-lighter ml-1"
                [tp]="'Drops the world map to 10 FPS while the game window is not focused, such as when it is on another monitor.'"
                [tpPlacement]="'top'"
              />
            </span>
            <input
              type="checkbox"
              [checked]="getOption('lowerFrameRateWhenUnfocused')"
              class="checkbox"
              (click)="
                setOption(
                  'lowerFrameRateWhenUnfocused',
                  !getOption('lowerFrameRateWhenUnfocused')
                )
              "
            />
          </label>
        </div>
      </div>
    </div>

    <div class="flex-1 flex flex-col gap-4">
      <div class="card shadow-sm">
        <div class="card-body p-3 gap-2">
          <div class="type-section-title">Audio</div>

          <div class="form-control">
            <label class="label cursor-pointer label-option">
              <span class="label-text">Master Volume</span>
              <input
                #masterVolume
                type="range"
                [min]="0"
                [max]="1"
                [step]="0.05"
                [value]="getOption('masterVolume')"
                (change)="setOption('masterVolume', masterVolume.valueAsNumber)"
                class="range"
              />
              <span class="ml-2">
                {{ getOption('masterVolume') * 100 | number: '1.0-0' }}%
              </span>
            </label>
          </div>

          <div class="form-control">
            <label class="label cursor-pointer label-option">
              <span class="label-text">Play Background Music</span>
              <input
                type="checkbox"
                [checked]="getOption('bgmPlay')"
                class="checkbox"
                (click)="setOption('bgmPlay', !getOption('bgmPlay'))"
              />
            </label>
          </div>

          @if (getOption('bgmPlay')) {
            <div class="form-control">
              <label class="label cursor-pointer label-option">
                <span class="label-text pl-6">Volume</span>
                <input
                  #bgmVolume
                  type="range"
                  [min]="0"
                  [max]="1"
                  [step]="0.05"
                  [value]="getOption('bgmVolume')"
                  (change)="setOption('bgmVolume', bgmVolume.valueAsNumber)"
                  class="range"
                />
                <span class="ml-2">
                  {{ getOption('bgmVolume') * 100 | number: '1.0-0' }}%
                </span>
              </label>
            </div>
          }

          <div class="form-control">
            <label class="label cursor-pointer label-option">
              <span class="label-text">Play Sound Effects</span>
              <input
                type="checkbox"
                [checked]="getOption('sfxPlay')"
                class="checkbox"
                (click)="setOption('sfxPlay', !getOption('sfxPlay'))"
              />
            </label>
          </div>

          @if (getOption('sfxPlay')) {
            <div class="form-control">
              <label class="label cursor-pointer label-option">
                <span class="label-text pl-6">Volume</span>
                <input
                  #sfxVolume
                  type="range"
                  [min]="0"
                  [max]="1"
                  [step]="0.05"
                  [value]="getOption('sfxVolume')"
                  (change)="setOption('sfxVolume', sfxVolume.valueAsNumber)"
                  class="range"
                />
                <span class="ml-2">
                  {{ getOption('sfxVolume') * 100 | number: '1.0-0' }}%
                </span>
              </label>
            </div>
          }

          <div class="form-control">
            <label class="label cursor-pointer label-option">
              <span class="label-text">Mute When Unfocused</span>
              <input
                type="checkbox"
                [checked]="getOption('muteWhenUnfocused')"
                class="checkbox"
                (click)="
                  setOption(
                    'muteWhenUnfocused',
                    !getOption('muteWhenUnfocused')
                  )
                "
              />
            </label>
          </div>
        </div>
      </div>
    </div>
  </div>
</div>
